import { memo, useMemo } from 'react';
import { Box, Divider, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { InvestmentDTO } from '@inversor-hub/types';
import { formatCurrency } from '@/utils/misc';
import { GlossaryIcon } from '../../shared/GlossaryHelp';
import { cardSurface } from '../../shared/surfaces';

interface CapexBreakdownProps {
  investment: InvestmentDTO;
}

const ROW_HELP: Record<string, string> = {
  'Precio de compra':
    'Importe pagado por adquirir la propiedad, sin incluir impuestos, honorarios ni reformas.',
  'Gastos asociados':
    'Impuestos, notaría, registro, agencia, gastos legales y otros costes de adquisición.',
  Reformas: 'Costes destinados a reforma, adecuación o mejora de la propiedad.',
};

export const CapexBreakdown = memo(({ investment }: CapexBreakdownProps) => {
  const { capex } = investment;

  const rows = useMemo(() => {
    const associatedExpenses =
      (capex.itpTax ?? 0) +
      (capex.notaryFees ?? 0) +
      (capex.registryFees ?? 0) +
      (capex.agencyFees ?? 0) +
      (capex.legalFees ?? 0) +
      (capex.otherTransactionCosts ?? 0);

    return (
      [
        { label: 'Precio de compra', amount: capex.acquisitionPrice, color: 'primary' as const },
        { label: 'Gastos asociados', amount: associatedExpenses, color: 'warning' as const },
        { label: 'Reformas', amount: capex.renovationCosts ?? 0, color: 'info' as const },
      ] as const
    ).filter((row) => row.amount > 0);
  }, [capex]);

  return (
    <Paper elevation={0} sx={{ p: 3, height: '100%', ...cardSurface }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Lo que costó
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Todo el dinero puesto en el inmueble, no solo el precio de compra.
          </Typography>
        </Box>
        <Box textAlign={{ xs: 'left', sm: 'right' }} sx={{ flexShrink: 0 }}>
          <Typography variant="overline" color="text.secondary" display="block" lineHeight={1}>
            Inversión total
            <GlossaryIcon term="capex" />
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {formatCurrency(capex.total)}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Stack spacing={2.5}>
        {rows.map((row) => {
          const pct = capex.total > 0 ? (row.amount / capex.total) * 100 : 0;

          return (
            <Box key={row.label}>
              <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
                <Tooltip title={ROW_HELP[row.label] ?? ''} arrow>
                  <Typography variant="body2" fontWeight={500} sx={{ cursor: 'help' }}>
                    {row.label}
                  </Typography>
                </Tooltip>
                <Box display="flex" gap={1.5} alignItems="baseline">
                  <Typography variant="body2" color="text.secondary">
                    {pct.toFixed(1)}%
                  </Typography>
                  <Typography variant="body1" fontWeight={600} minWidth={90} textAlign="right">
                    {formatCurrency(row.amount)}
                  </Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(pct, 100)}
                color={row.color}
                sx={{ height: 6, borderRadius: 3 }}
                aria-label={`${row.label}: ${formatCurrency(row.amount)}`}
              />
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
});

CapexBreakdown.displayName = 'CapexBreakdown';
